import { useSettings } from '../../core/settings/use-settings';
import { Link } from 'react-router-dom';

interface Props {
  className?: string;
  padding?: string;
}

const QUICK_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How it Works', href: '#how-it-works' },
  { label: "FAQ's", href: '#faq' },
];

const COMPANY_LINKS = [
  { label: 'Contact us', href: '/contact' },
  { label: 'Terms of Service', href: '/pages/terms-of-service' },
  { label: 'Privacy Policy', href: '/pages/privacy-policy' },
  { label: 'Report', href: '/pages/report-abuse' },
  { label: 'Sitemap', href: '/sitemap.xml' },
];

const ALTERNATIVE_LINKS = [
  { label: 'Linktree alternative', href: '/alternatives/linktree' },
  { label: 'View all alternatives', href: '/alternatives' },
  { label: 'Link in bio for creators', href: '/for' },
  { label: 'Use cases', href: '/use-cases' },
  { label: 'Platforms', href: '/platforms' },
  { label: 'Features', href: '/features' },
];

const SOCIAL_LINKS = [
  {
    label: 'LinkedIn', href: 'https://linkedin.com/company/linkinbiosite',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>,
  },
  {
    label: 'YouTube', href: 'https://youtube.com/channel/linkinbiosite',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.55 3.5 12 3.5 12 3.5s-7.55 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.04 0 12 0 12s0 3.96.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.45 20.5 12 20.5 12 20.5s7.55 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.96 24 12 24 12s0-3.96-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" /></svg>,
  },
  {
    label: 'Twitter / X', href: 'https://x.com/linkinbiosite',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
  },
  {
    label: 'Instagram', href: 'https://instagram.com/linkinbiosite',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" /></svg>,
  },
];

const linkStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: 'rgba(255,255,255,0.5)',
  textDecoration: 'none',
  display: 'block',
  transition: 'color 0.2s',
};

function FooterLink({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) {
  const style = linkStyle;
  const handlers = {
    onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = '#fff'; },
    onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; },
  };
  if (external || href.startsWith('#')) {
    return <a href={href} style={style} {...handlers}>{children}</a>;
  }
  return <Link to={href} style={style} {...handlers}>{children}</Link>;
}

export function Footer(_props: Props) {
  const year = new Date().getFullYear();
  const { branding } = useSettings();

  return (
    <footer style={{ background: '#0e0e0e', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ maxWidth: 1140, margin: '0 auto', padding: 'clamp(40px, 6vw, 72px) clamp(20px, 5vw, 32px) 0' }}>

        {/* Top grid — 4 columns */}
        <div className="footer-grid" style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr',
          gap: '40px 48px',
          alignItems: 'flex-start',
        }}>

          {/* Col 1 — Brand */}
          <div>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 20, textDecoration: 'none' }}>
              {branding.logo_light ? (
                <img src={branding.logo_light} alt={branding.site_name} style={{ height: 32, width: 'auto', display: 'block' }} />
              ) : (
                <span style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{branding.site_name}</span>
              )}
            </Link>
            <p className="footer-desc" style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', lineHeight: 1.85, margin: '0 0 28px', maxWidth: 400 }}>
              LinkInBio helps creators, marketers, and businesses manage their online presence through smarter link sharing. From shortening long URLs to building a fully customisable bio link page, the platform gives you complete visibility into how your links perform, with click data, geographic insights, and device breakdowns available for every URL you create.
            </p>

            {/* Social icons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {SOCIAL_LINKS.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 34, height: 34,
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.45)',
                    textDecoration: 'none',
                    flexShrink: 0,
                    transition: 'border-color 0.2s, color 0.2s, background 0.2s',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.borderColor = '#ff4f00';
                    el.style.color = '#ff4f00';
                    el.style.background = 'rgba(255,79,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.borderColor = 'rgba(255,255,255,0.1)';
                    el.style.color = 'rgba(255,255,255,0.45)';
                    el.style.background = 'transparent';
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 20, marginTop: 0 }}>
              Quick Links
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {QUICK_LINKS.map(link => (
                <li key={link.label}>
                  <FooterLink href={link.href} external>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Company */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 20, marginTop: 0 }}>
              Company
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {COMPANY_LINKS.map(link => (
                <li key={link.label}>
                  <FooterLink href={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Resources */}
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 20, marginTop: 0 }}>
              Resources
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {ALTERNATIVE_LINKS.map(link => (
                <li key={link.label}>
                  <FooterLink href={link.href}>{link.label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Divider */}
        <div className="footer-bottom" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 32, padding: '20px 0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.2)' }}>
            © {year} {branding.site_name} · All rights reserved
          </span>
          <a
            href="/register"
            style={{ fontSize: 12.5, fontWeight: 600, color: '#ff4f00', textDecoration: 'none', padding: '6px 16px', border: '1px solid rgba(255,79,0,0.4)', borderRadius: 24, letterSpacing: '0.01em', transition: 'background 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,79,0,0.1)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
          >
            Get started free →
          </a>
        </div>

      </div>

      <style>{`
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .footer-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 24px 16px !important;
            margin-bottom: 24px !important;
          }
          .footer-grid > div:first-child {
            grid-column: 1 / -1;
          }
          .footer-bottom {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .footer-desc {
            font-size: 12px !important;
            line-height: 1.7 !important;
          }
        }
      `}</style>
    </footer>
  );
}
